import {useEffect, useRef, useState} from 'react';
import {Button, Flex, useTheme, View} from '@aws-amplify/ui-react';
import LogoMark from "./LogoMark";

const DEFAULT_MINUTES = 45;

function pad(n: number): string {
    return String(Math.max(0, n)).padStart(2, '0');
}

function DoneModal({onClose}: { onClose: () => void }) {
    return (
        <div className="done-modal" role="dialog" aria-modal="true" aria-labelledby="done-modal-title">
            <div className="done-modal__card">
                <LogoMark/>
                <h2 id="done-modal-title" className="done-modal__title">Time to take a break</h2>
                <p className="done-modal__text">Hit Go! to pick a break activity.</p>
                <Button variation="primary" onClick={onClose}>OK</Button>
            </div>
        </div>
    );
}

interface WorkTimerProps {
    onStart?: () => void;
}

export default function WorkTimer({onStart}: WorkTimerProps) {
    const {tokens} = useTheme();
    const [seconds, setSeconds] = useState(DEFAULT_MINUTES * 60);
    const [isRunning, setIsRunning] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [inputMins, setInputMins] = useState(String(DEFAULT_MINUTES));
    const [inputSecs, setInputSecs] = useState('0');
    const [showModal, setShowModal] = useState(false);
    const minsRef = useRef<HTMLInputElement>(null);
    const secsRef = useRef<HTMLInputElement>(null);

    // Countdown using functional updater to avoid stale closure
    useEffect(() => {
        if (!isRunning) return;
        const id = setInterval(() => setSeconds(s => s - 1), 1000);
        return () => clearInterval(id);
    }, [isRunning]);

    function playBellSound() {
        try {
            const ctx = new AudioContext();
            const now = ctx.currentTime;
            [0, 0.1, 0.2].forEach((delay, i) => {
                const osc = ctx.createOscillator();
                const gain = ctx.createGain();
                osc.connect(gain);
                gain.connect(ctx.destination);
                osc.type = 'sine';
                osc.frequency.value = i === 0 ? 880 : i === 1 ? 1100 : 1320;
                gain.gain.setValueAtTime(0.4, now + delay);
                gain.gain.exponentialRampToValueAtTime(0.001, now + delay + 1.2);
                osc.start(now + delay);
                osc.stop(now + delay + 1.2);
            });
        } catch {
            // AudioContext not supported
        }
    }

    // Detect timer reaching zero in a separate effect
    useEffect(() => {
        if (isRunning && seconds <= 0) {
            setIsRunning(false);
            setShowModal(true);
            playBellSound();
            if ('Notification' in window && Notification.permission === 'granted') {
                new Notification('Time to take a break', {
                    body: 'Hit Go! to pick a break activity.',
                    requireInteraction: true,
                });
            }
        }
    }, [seconds, isRunning]);

    useEffect(() => {
        if (isEditing) minsRef.current?.focus();
    }, [isEditing]);

    function handleTimerClick() {
        if (!isRunning) {
            const m = Math.floor(Math.max(0, seconds) / 60);
            const s = Math.max(0, seconds) % 60;
            setInputMins(String(m).padStart(2, '0'));
            setInputSecs(String(s).padStart(2, '0'));
            setIsEditing(true);
        }
    }

    function padNum(val: string, max: number): string {
        const n = Math.min(max, Math.max(0, parseInt(val, 10) || 0));
        return String(n).padStart(2, '0');
    }

    function commitEdit(minsVal = inputMins, secsVal = inputSecs) {
        const m = Math.min(90, Math.max(0, parseInt(minsVal, 10) || 0));
        const s = Math.min(59, Math.max(0, parseInt(secsVal, 10) || 0));
        const total = Math.max(1, m * 60 + s);
        setSeconds(total);
        setIsEditing(false);
    }

    function jumpToSecs() {
        const padded = padNum(inputMins, 90);
        setInputMins(padded);
        secsRef.current?.focus();
        secsRef.current?.select();
    }

    function jumpToMins() {
        const padded = padNum(inputSecs, 59);
        setInputSecs(padded);
        minsRef.current?.focus();
        minsRef.current?.select();
    }

    function handleMinsKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
        if (e.key === 'Tab') {
            e.preventDefault();
            jumpToSecs();
        } else if (e.key === 'ArrowRight' && e.currentTarget.selectionStart === e.currentTarget.value.length) {
            e.preventDefault();
            jumpToSecs();
        } else if (e.key === 'Enter') {
            commitEdit();
        } else if (e.key === 'Escape') {
            setIsEditing(false);
        }
    }

    function handleSecsKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
        if (e.key === 'ArrowLeft' && e.currentTarget.selectionStart === 0) {
            e.preventDefault();
            jumpToMins();
        } else if (e.key === 'Enter') {
            commitEdit();
        } else if (e.key === 'Escape') {
            setIsEditing(false);
        }
    }

    function handleStartStop() {
        const starting = !isRunning;
        if (starting) {
            onStart?.();
            if ('Notification' in window && Notification.permission === 'default') {
                Notification.requestPermission();
            }
        }
        setIsRunning(starting);
    }

    const mins = Math.floor(Math.max(0, seconds) / 60);
    const secs = Math.max(0, seconds) % 60;

    return (
        <>
            {showModal && (
                <DoneModal onClose={() => {
                    setShowModal(false);
                    setSeconds(DEFAULT_MINUTES * 60);
                }}/>
            )}
            <View
                borderRadius={tokens.radii.large}
                padding={tokens.space.medium}
                marginBottom={tokens.space.medium}
                backgroundColor={tokens.colors.background.primary}
                border={`1px solid ${tokens.colors.border.secondary}`}
            >
                <Flex direction="row" alignItems="center" justifyContent="center" gap={tokens.space.large}>
                    {isEditing ? (
                        <div className="timer-edit timer-digits timer-digits--running">
                            <input
                                ref={minsRef}
                                type="text"
                                inputMode="numeric"
                                maxLength={2}
                                value={inputMins}
                                onChange={e => {
                                    const v = e.target.value.replace(/\D/g, '');
                                    setInputMins(v);
                                    if (v.length === 2) {
                                        secsRef.current?.focus();
                                        secsRef.current?.select();
                                    }
                                }}
                                onBlur={() => {
                                    setTimeout(() => {
                                        if (document.activeElement !== secsRef.current &&
                                            document.activeElement !== minsRef.current) {
                                            commitEdit();
                                        }
                                    }, 100);
                                }}
                                onFocus={e => e.target.select()}
                                onKeyDown={handleMinsKeyDown}
                            />
                            <span>:</span>
                            <input
                                ref={secsRef}
                                type="text"
                                inputMode="numeric"
                                maxLength={2}
                                value={inputSecs}
                                onChange={e => setInputSecs(e.target.value.replace(/\D/g, ''))}
                                onBlur={() => {
                                    setTimeout(() => {
                                        if (document.activeElement !== minsRef.current &&
                                            document.activeElement !== secsRef.current) {
                                            commitEdit();
                                        }
                                    }, 100);
                                }}
                                onFocus={e => e.target.select()}
                                onKeyDown={handleSecsKeyDown}
                            />
                        </div>
                    ) : (
                        <span
                            className={'timer-digits ' + (isRunning ? 'timer-digits--running' : 'timer-digits--editable')}
                            onClick={handleTimerClick}
                            title={isRunning ? undefined : 'Click to set time'}
                        >
                            {pad(mins)}:{pad(secs)}
                        </span>
                    )}
                    {/* Default (ink outline) on purpose: Go! below is the page's one coral highlight */}
                    <Button onClick={handleStartStop}>
                        {isRunning ? 'Stop' : 'Start'}
                    </Button>
                </Flex>
            </View>
        </>
    );
}
